import { useSettingsStore } from "@/stores/settings";

type PointOfSaleQuery = APIOutputs["pointOfSales"]["getOnePOS"];
export type ZatcaDeviceQuery = APIOutputs["pointOfSales"]["getZatcaDevice"];

export type PosSchema = {
  id: string;
  pointOfSale: PointOfSaleQuery["data"];
  reference: PointOfSaleQuery["references"];
  zatcaSettings: PointOfSaleQuery["zatcaSettings"];
  cashAccountLastRunningTotal: APIOutputs["ledger"]["general"]["getRunningTotal"]["data"][number];
  zatcaDevice_production?: ZatcaDeviceQuery;
  zatcaDevice_simulation?: ZatcaDeviceQuery;
};

const { generateReference, isNetworkError } = useUtils();

export const usePosStore = defineStore("pos", () => {
  // states
  const pointOfSale = ref<PosSchema["pointOfSale"]>();
  const posReference = ref<PosSchema["reference"]>();
  const cashAccountLastRunningTotal =
    ref<PosSchema["cashAccountLastRunningTotal"]>();
  const posLogoUrl = ref<string>();

  const zatcaSettings = ref<PointOfSaleQuery["zatcaSettings"]>({
    enabled: false,
    wentLiveAt: null,
    isProduction: false,
    zatcaDeviceId: undefined,
  });
  const zatcaDevice = ref<ZatcaDeviceQuery>();

  // computed
  const hasPaymentMethods = computed(
    () =>
      (pointOfSale.value?.organization?.receiptPaymentMethods.length || 0) > 0
  );

  const posTitle = computed(
    () =>
      `${pointOfSale.value?.name} - ${pointOfSale.value?.organization?.name}`
  );

  const isZatcaActive = computed(
    () =>
      zatcaSettings.value.enabled === true &&
      zatcaSettings.value.wentLiveAt != null &&
      zatcaDevice.value != null
  );

  const canConnectToZatcaDevice = computed(
    () =>
      zatcaSettings.value.enabled === true &&
      zatcaSettings.value.wentLiveAt != null &&
      zatcaDevice.value == null
  );

  // composables
  const { cache } = useIndexDB();

  // fetch pointOfSale handler
  const fetchPointOfSale = async (id: string) => {
    const { trpcClient } = useTrpcClient();

    const { data, references, zatcaSettings } =
      await trpcClient.pointOfSales.getOnePOS.query({
        id,
      });

    return {
      pointOfSale: data,
      posReference: references,
      zatcaSettings,
    };
  };

  // fetch cashAccountLastRunningTotal handler
  const fetchCashAccountLastRunningTotal = async ({
    cashAccountId,
  }: {
    cashAccountId: string;
  }) => {
    const { trpcClient } = useTrpcClient();

    const { data } = await trpcClient.ledger.general.getRunningTotal
      .query({
        where: {
          accountId: cashAccountId,
          isConfirmed: true,
          transactionDate: { gte: undefined, lte: undefined },
        },
        limit: 1,
      })
      .catch(() => {
        // todo: create separate endpoint for this later
        console.debug("Reports permission is disabled for user.");
        return { data: [] };
      });

    return data?.[0] ?? undefined;
  };

  // update cashAccountLastRunningTotal handler
  const updateCashAccountLastRunningTotal = async () => {
    const { settings } = useSettingsStore();

    try {
      if (!settings || !pointOfSale.value) {
        throw new Error("Settings not found");
      }

      const runningTotal = await fetchCashAccountLastRunningTotal({
        cashAccountId: pointOfSale.value.cashAccountId,
      });

      cashAccountLastRunningTotal.value = runningTotal;
      await cache.pos.updateProperty(
        "cashAccountLastRunningTotal",
        runningTotal
      );
    } catch (error) {
      console.error("Error updating cash account last running total:", error);
    }
  };

  // fetch data handler
  const fetchData = async () => {
    const { settings } = useSettingsStore();
    const { logout } = useAuthStore();
    const { clear } = useAppLifeCycle();
    const { trpcClient } = useTrpcClient();

    let posData: PosSchema | undefined;

    try {
      if (!settings) {
        throw new Error("Settings not found");
      }

      const fetchedPosData = await fetchPointOfSale(settings.posId);

      const cashAccountLastRunningTotal =
        await fetchCashAccountLastRunningTotal({
          cashAccountId: fetchedPosData.pointOfSale.cashAccountId,
        });

      // get reference from cache
      const posCache = await cache.pos.get();

      // if reference is not available, use the one from the server
      // if reference is available, use the one from the cache (maintain the counter)
      const reference = posCache?.reference ?? fetchedPosData.posReference;

      posData = {
        cashAccountLastRunningTotal,
        pointOfSale: fetchedPosData.pointOfSale,
        id: fetchedPosData.pointOfSale.id,
        reference,
        zatcaSettings: fetchedPosData.zatcaSettings,
        zatcaDevice_production: posCache?.zatcaDevice_production,
        zatcaDevice_simulation: posCache?.zatcaDevice_simulation,
      };

      const zatcaEnv =
        fetchedPosData.zatcaSettings.isProduction === true
          ? "production"
          : "simulation";

      try {
        // check if to connect to zatca device or not
        // 1. connect if the zatcaDevice is available on the server, and not available locally or not the same
        // 2. disconnect if the zatcaDevice is available locally, and not available on the server
        if (
          fetchedPosData.zatcaSettings.zatcaDeviceId != null &&
          (posData[`zatcaDevice_${zatcaEnv}`] == null ||
            posData[`zatcaDevice_${zatcaEnv}`]?.id !==
              fetchedPosData.zatcaSettings.zatcaDeviceId)
        ) {
          const data = await trpcClient.pointOfSales.getZatcaDevice.query({
            posId: posData.id,
          });

          posData[`zatcaDevice_${zatcaEnv}`] = data;
        }

        // disconnect if the zatcaDevice is available locally, and not available on the server
        if (fetchedPosData.zatcaSettings.zatcaDeviceId == null) {
          posData[`zatcaDevice_${zatcaEnv}`] = undefined;
        }
      } catch (error) {
        posData[`zatcaDevice_${zatcaEnv}`] = undefined;
        console.error("Error fetching zatca device:", error);
      }

      // cache data
      await cache.pos.save(fetchedPosData.pointOfSale.id, posData);
    } catch (error) {
      if (isNetworkError(error)) {
        // if network error, use cached data
        const cachedData = await cache.pos.get();

        posData = cachedData;
      } else if ((error as APIError).data?.code === "NOT_FOUND") {
        // if pos not found, clear all cache
        await clear();
      }
    } finally {
      // if data is available, set it
      if (posData) {
        pointOfSale.value = posData.pointOfSale;
        posReference.value = posData.reference;
        cashAccountLastRunningTotal.value = posData.cashAccountLastRunningTotal;
        zatcaSettings.value = posData.zatcaSettings;
        zatcaDevice.value =
          posData[
            `zatcaDevice_${
              posData.zatcaSettings.isProduction === true
                ? "production"
                : "simulation"
            }`
          ];

        // create image tag for logo to pre-cache it
        if (posData.pointOfSale.organization.organizationLogosUrls.posLogoUrl) {
          await fetch(
            posData.pointOfSale.organization.organizationLogosUrls.posLogoUrl,
            {
              mode: "no-cors",
            }
          );
          posLogoUrl.value =
            posData.pointOfSale.organization.organizationLogosUrls.posLogoUrl;
        }
      } else {
        logout();
      }
    }
  };

  // increment reference handler
  const incrementReferences = async ({
    transactionDate,
    disableInvoice = false,
    disableReceipt = false,
    noOfReceipts = 1,
  }: {
    transactionDate: string;
    disableInvoice?: boolean;
    disableReceipt?: boolean;
    noOfReceipts?: number;
  }) => {
    if (!pointOfSale.value || !posReference.value)
      throw new Error("POS is invalid");

    const posReferenceData = { ...posReference.value };

    let invoiceReference: string | undefined;
    let receiptReferences: string[] | undefined;

    if (!disableInvoice) {
      invoiceReference = generateReference({
        keyPath: "posInvoices",
        referenceData: posReferenceData,
        transactionDate,
      });
      // increment counter
      posReferenceData.posInvoicesReferenceCounter += 1;
    }

    if (!disableReceipt) {
      receiptReferences = Array.from({ length: noOfReceipts }, (_, i) => {
        const receiptReference = generateReference({
          keyPath: "posReceipts",
          referenceData: {
            ...posReferenceData,
            posReceiptsReferenceCounter:
              posReferenceData.posReceiptsReferenceCounter + i,
          },
          transactionDate,
        });

        return receiptReference;
      });

      // increment counter
      posReferenceData.posReceiptsReferenceCounter += noOfReceipts;
    }

    await cache.pos.updateProperty("reference", posReferenceData);

    posReference.value = posReferenceData;

    return { invoiceReference, receiptReferences };
  };

  const incrementZatcaDevice = async ({
    invoiceHash,
  }: {
    invoiceHash: string;
  }) => {
    if (pointOfSale.value == null || isZatcaActive.value === false)
      throw new Error("POS or Zatca device is invalid");

    const zatcaDeviceData = { ...zatcaDevice.value } as ZatcaDeviceQuery;

    zatcaDeviceData.lastInvoiceHash = invoiceHash;
    zatcaDeviceData.lastInvoiceCounter =
      (zatcaDeviceData.lastInvoiceCounter ?? 0) + 1;

    await cache.pos.updateProperty(
      `zatcaDevice_${
        zatcaSettings.value.isProduction === true ? "production" : "simulation"
      }`,
      JSON.parse(JSON.stringify(zatcaDeviceData))
    );

    zatcaDevice.value = zatcaDeviceData;
  };

  // clear
  const clear = async () => {
    await cache.pos.clear();
    pointOfSale.value = undefined;
    posReference.value = undefined;
    cashAccountLastRunningTotal.value = undefined;
  };

  // init
  const init = async () => {
    await fetchData();
  };

  return {
    // getters
    pointOfSale,
    posReference,
    cashAccountLastRunningTotal,
    hasPaymentMethods,
    posLogoUrl,
    posTitle,
    zatcaSettings,
    zatcaDevice,
    isZatcaActive,
    canConnectToZatcaDevice,
    // setters
    clear,
    init,
    incrementReferences,
    fetchData,
    updateCashAccountLastRunningTotal,
    incrementZatcaDevice,
  };
});
